import { ChevronDownIcon, Trash2Icon } from 'lucide-react';

import { getMuscleColor } from '@/entities/muscle/model/muscle-colors.utils';
import { cn } from '@/lib/utils';

interface StepPlanHeaderProps {
	movementName: string;
	primaryMuscleName: string;
	stepOrderIndex: number;
	onDeleteStepPlan: () => void;
	equipmentName: string;
	isCollapsed: boolean;
	onExpandStepPlan: () => void;
}

export function StepPlanHeader({
	movementName,
	primaryMuscleName,
	stepOrderIndex,
	onDeleteStepPlan,
	equipmentName,
	isCollapsed,
	onExpandStepPlan,
}: StepPlanHeaderProps) {
	// Get consistent colors for this muscle
	const muscleColors = getMuscleColor(primaryMuscleName);

	return (
		// biome-ignore lint/a11y/useSemanticElements: we have nested buttons
		<div
			role="button"
			tabIndex={0}
			onClick={onExpandStepPlan}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === 'Space') {
					onExpandStepPlan();
				}
			}}
			className="flex w-full items-start justify-between bg-white px-4 py-3 transition-colors duration-100 hover:bg-gray-50/50 active:bg-gray-50 sm:px-5 sm:py-3.5"
		>
			<div className="flex items-start gap-3">
				{/*--- Step Order Badge ---*/}
				<div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-900 font-semibold text-white text-xs shadow-sm sm:size-7">
					{stepOrderIndex + 1}
				</div>

				{/*--- Exercise Information ---*/}
				<div className="min-w-0 flex-1 space-y-0.5">
					<h4 className="font-semibold text-base text-gray-900 leading-tight sm:text-lg">
						{movementName}
					</h4>
					<div className="flex items-center gap-1.5">
						<span className="font-medium text-gray-500 text-xs">{equipmentName}</span>
						{primaryMuscleName && (
							<>
								<span className="text-gray-300 text-xs">•</span>
								<span className={cn('font-medium text-xs', muscleColors.text)}>
									{primaryMuscleName}
								</span>
							</>
						)}
					</div>
				</div>
			</div>
			<div className="flex items-center gap-2">
				<button
					type="button"
					className="group flex size-7 shrink-0 cursor-pointer items-center justify-center rounded bg-gray-100 hover:bg-red-50 sm:end-5 sm:top-5 sm:size-8"
					onClick={(e) => {
						e.stopPropagation();
						onDeleteStepPlan();
					}}
				>
					<Trash2Icon className="size-3.5 text-gray-500 group-hover:text-red-500 sm:size-4" />
				</button>
				<div className="flex size-7 shrink-0 items-center justify-center rounded-md text-gray-400">
					<ChevronDownIcon
						className={cn(
							'size-4 transition-transform duration-200 sm:size-5',
							isCollapsed ? 'rotate-0' : 'rotate-180',
						)}
					/>
				</div>
			</div>
		</div>
	);
}
